/**
 * CatchballUndoAction — the shared "reason modal → governed reversal RPC →
 * toast → refetch" pattern for the three Catchball undo buttons (migration
 * 738 / src/lib/reversals/reversalsService.ts): Recall a ball nobody has
 * opened or claimed, Recall a whole fan-out, Withdraw an undecided proposal.
 *
 * The server is the sole authority on whether an undo succeeds — this
 * component never hides a refusal. On refusal it shows the server's `error`,
 * `blockedBy` (what consumed the thing) and `remedy` (what to do instead) and
 * leaves the modal open so the actor can read them; it does not retry or
 * silently fall back to anything.
 *
 * Reuses `../../ds/Modal` (ds/Modal.tsx) for the dialog chrome rather than a
 * new hand-rolled overlay — see that file's header for why it exists.
 */

import { useState, type MouseEvent } from 'react';
import { Modal } from '../../ds/Modal';
import { useToast } from '../../ui/Toast';
import type { ReversalResult } from '../../../lib/reversals/reversalsService';

export interface CatchballUndoActionProps {
  /** Text on the trigger. Keep it short — "Recall", "Recall fan-out", "Withdraw proposal". */
  triggerLabel: string;
  /** Dialog title, e.g. "Recall this ball". */
  modalTitle: string;
  /** Sentence of context under the title. */
  description?: string;
  /** Text on the confirm button once a reason is entered. */
  confirmLabel: string;
  reasonPlaceholder?: string;
  /** Calls the typed reversalsService wrapper (already validated: non-empty reason required server-side too). */
  onConfirm: (reason: string) => Promise<ReversalResult>;
  /** Re-fetch whatever list/detail is showing this ball or proposal. */
  onSuccess?: () => void;
  successTitle?: string;
  successMessage?: string;
  /** Trigger button styling. Defaults to a low-emphasis text button. */
  className?: string;
  disabled?: boolean;
  /** Wraps the trigger's onClick to stop it bubbling into a parent <Link>/row click. */
  stopPropagation?: boolean;
}

const DEFAULT_TRIGGER_CLASS =
  'text-xs font-medium text-[var(--text-ter)] hover:text-[var(--coral)] dark:text-[var(--text-dim)] dark:hover:text-[var(--coral)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

export function CatchballUndoAction({
  triggerLabel,
  modalTitle,
  description,
  confirmLabel,
  reasonPlaceholder = 'Why are you undoing this? (required)',
  onConfirm,
  onSuccess,
  successTitle = 'Undone',
  successMessage,
  className,
  disabled = false,
  stopPropagation = true,
}: CatchballUndoActionProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<{ error?: string; blockedBy?: string; remedy?: string } | null>(null);
  const { showToast } = useToast();

  const close = () => {
    if (busy) return;
    setOpen(false);
    setReason('');
    setRefusal(null);
  };

  const openModal = (e: MouseEvent) => {
    if (stopPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }
    setRefusal(null);
    setOpen(true);
  };

  const handleConfirm = async () => {
    if (!reason.trim() || busy) return;
    setBusy(true);
    try {
      const result = await onConfirm(reason.trim());
      if (result.ok) {
        setOpen(false);
        setReason('');
        setRefusal(null);
        showToast('success', successTitle, successMessage);
        onSuccess?.();
      } else {
        // Never hide a refusal: keep the modal open with the server's own words.
        setRefusal({ error: result.error, blockedBy: result.blockedBy, remedy: result.remedy });
      }
    } catch (err) {
      setRefusal({ error: err instanceof Error ? err.message : 'The reversal failed.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        disabled={disabled}
        className={className ?? DEFAULT_TRIGGER_CLASS}
      >
        {triggerLabel}
      </button>

      <Modal
        open={open}
        onClose={close}
        title={modalTitle}
        description={description}
        size="sm"
        footer={
          <>
            <button
              type="button"
              onClick={close}
              disabled={busy}
              className="border border-[var(--rule)] px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleConfirm()}
              disabled={busy || !reason.trim()}
              className="bg-[var(--coral)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--coral)] disabled:opacity-50"
            >
              {busy ? 'Working…' : confirmLabel}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          {refusal && (
            <div className="border border-[var(--coral)] bg-[var(--coral-dim)] p-3 text-sm text-[var(--coral)] space-y-1">
              <p className="font-medium">{refusal.error ?? 'This could not be undone.'}</p>
              {refusal.blockedBy && (
                <p className="text-xs opacity-80">Blocked by: {refusal.blockedBy}</p>
              )}
              {refusal.remedy && <p className="text-xs">{refusal.remedy}</p>}
            </div>
          )}
          <label className="block text-sm font-medium text-[var(--text-pri)]">
            Reason <span className="font-normal text-[var(--text-ter)]">(required — canon 27 §6)</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={reasonPlaceholder}
            className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none bg-[var(--bg-raised)]"
          />
        </div>
      </Modal>
    </>
  );
}
